import {
  Box,
  BoxProps,
  Button,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  useCustomToast,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useCreateExclusiveDealApi, useGetExclusiveDealApi, useUpdateExclusiveDealApi } from 'apis/exclusive-deals';
import { useGetSupplierProductsApi, useSearchSuppliersApi } from 'apis/supplier';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

type TOption = {
  value: number | string;
  label: string;
};

type TSupplierOption = TOption & {
  countryCode?: string;
};

type TProductOption = TOption & {
  defaultPrice?: number;
  defaultUnit?: string;
};

type TInputs = {
  supplierId: TSupplierOption | null;
  productId: TProductOption | null;
  cashbackPercent: string;
  dealPrice: string;
  productExpiryDate: string;
  isNearExpiry: boolean;
};

const defaultValues: TInputs = {
  supplierId: null,
  productId: null,
  cashbackPercent: '',
  dealPrice: '',
  productExpiryDate: '',
  isNearExpiry: false,
};

export interface IAddExclusiveDealModalCell extends BoxProps {
  variant: 'add' | 'edit';
  trigger: JSX.Element;
  deal?: any;
}

export const AddExclusiveDealModal = ({ variant, trigger, deal }: IAddExclusiveDealModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [supplierSearch, setSupplierSearch] = useState('');
  const toast = useCustomToast();

  const isEdit = useMemo(() => variant === 'edit', [variant]);

  // Fetch deal details when editing
  const { data: dealDetailsData, isLoading: isDealLoading } = useGetExclusiveDealApi(deal?.id, {
    enabled: isOpen && isEdit && !!deal?.id,
  });
  const dealDetails = dealDetailsData?.data;

  const { data: suppliersData, isLoading: isSuppliersLoading } = useSearchSuppliersApi(supplierSearch, {
    enabled: isOpen && !isEdit && supplierSearch.length >= 2,
  });
  const { isLoading: isCreateLoading, mutate: createDeal } = useCreateExclusiveDealApi();
  const { isLoading: isUpdateLoading, mutate: updateDeal } = useUpdateExclusiveDealApi();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TInputs>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  // Watch supplierId to fetch products
  const selectedSupplier = useWatch({ control, name: 'supplierId' });
  const supplierId = selectedSupplier?.value ? Number(selectedSupplier.value) : null;

  const { data: productsData, isLoading: isProductsLoading } = useGetSupplierProductsApi(supplierId, undefined, {
    enabled: !!supplierId,
  });

  const supplierOptions: TSupplierOption[] = useMemo(() => {
    const suppliers = suppliersData?.data || [];
    return suppliers.map((s) => ({
      value: s.id,
      label: s.name,
      countryCode: s.countryCode,
    }));
  }, [suppliersData]);

  const productOptions: TProductOption[] = useMemo(() => {
    const products = productsData?.data || [];
    return products.map((p) => ({
      value: p.id,
      label: p.name?.en || `Product #${p.id}`,
      defaultPrice: p.defaultPrice ?? p.price,
      defaultUnit: p.defaultUnit,
    }));
  }, [productsData]);

  // Watch selected product for default price info
  const selectedProduct = useWatch({ control, name: 'productId' }) as TProductOption | null;

  // Watch isNearExpiry for controlled switch
  const isNearExpiryValue = useWatch({ control, name: 'isNearExpiry' });

  // Track previous supplier ID to detect actual changes
  const prevSupplierIdRef = useRef<number | null>(null);

  // Clear product only when supplier changes from one value to another
  useEffect(() => {
    if (!isEdit && prevSupplierIdRef.current !== null && prevSupplierIdRef.current !== supplierId) {
      setValue('productId', null);
    }
    prevSupplierIdRef.current = supplierId;
  }, [supplierId, isEdit, setValue]);

  // Update values when modal opens (for edit mode)
  const updateValues = () => {
    if (isEdit && dealDetails) {
      setValue('cashbackPercent', dealDetails?.cashbackPercent?.toString() ?? '');
      setValue('dealPrice', dealDetails?.dealPrice?.toString() ?? '');
      setValue('productExpiryDate', dealDetails?.productExpiryDate ?? '');
      setValue('isNearExpiry', dealDetails?.isNearExpiry ?? false);

      // Set supplier info for display in edit mode
      if (dealDetails?.product?.supplierId) {
        setValue('supplierId', {
          value: dealDetails.product.supplierId,
          label: dealDetails.supplier?.name ?? `Supplier #${dealDetails.supplier?.id ?? dealDetails.product.supplierId}`,
          countryCode: dealDetails.countryCode,
        });
      }

      // Set product info for display in edit mode
      if (dealDetails?.productId && dealDetails?.product) {
        setValue('productId', {
          value: dealDetails.productId,
          label: dealDetails.product.nameEn,
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen && isEdit && dealDetails) {
      updateValues();
    }
  }, [isOpen, isEdit, dealDetails]);

  const onSubmit = (data: TInputs) => {
    const cashbackPercent = data.cashbackPercent ? parseFloat(data.cashbackPercent) : undefined;
    const dealPrice = data.dealPrice ? parseFloat(data.dealPrice) : undefined;
    const productExpiryDate = data.productExpiryDate || null;
    const isNearExpiry = data.isNearExpiry ?? false;

    if (variant === 'add') {
      const supplierCountryCode = (data.supplierId as TSupplierOption)?.countryCode;
      const productIdValue = data.productId?.value;

      // Validate required fields before submission
      if (!productIdValue) {
        toast({
          description: 'Please select a product',
          status: 'error',
        });
        return;
      }

      if (!supplierCountryCode) {
        toast({
          description: 'Selected supplier does not have a country code',
          status: 'error',
        });
        return;
      }

      const variables = {
        body: {
          productId: Number(productIdValue),
          countryCode: supplierCountryCode,
          cashbackPercent,
          dealPrice,
          productExpiryDate,
          isNearExpiry,
        },
      };

      createDeal(variables, {
        onSuccess: () => {
          onCloseModal();
        },
      });
    }

    if (variant === 'edit') {
      const variables = {
        dealId: deal?.id,
        body: {
          cashbackPercent,
          dealPrice,
          productExpiryDate,
          isNearExpiry,
        },
      };

      updateDeal(variables, {
        onSuccess: () => {
          onCloseModal();
        },
      });
    }

    reset(data);
  };

  const onCloseModal = () => {
    onClose();
    reset(defaultValues);
    setSupplierSearch('');
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEdit ? 'Edit' : 'Add new'} exclusive deal</ModalHeader>
          <ModalBody>
            <Stack
              as='form'
              id='addExclusiveDealModal'
              spacing='lg'
              onSubmit={handleSubmit(onSubmit, (errors) => {
                console.error('Form validation errors:', errors);
                toast({
                  description: 'Please fill in all required fields',
                  status: 'error',
                });
              })}
              noValidate>
              {isEdit ? (
                isDealLoading ? (
                  <Box p='md' bg='gray.50' borderRadius='md' textAlign='center'>
                    <Text color='gray.500'>Loading deal details...</Text>
                  </Box>
                ) : (
                  <Box p='md' bg='gray.50' borderRadius='md'>
                    <Text fontWeight='semibold' mb='sm'>
                      Deal Information
                    </Text>
                    <HStack spacing='lg' flexWrap='wrap'>
                      <Box>
                        <Text fontSize='xs' color='gray.500'>
                          Product
                        </Text>
                        <Text fontWeight='medium'>{dealDetails?.product?.nameEn || '-'}</Text>
                      </Box>
                    </HStack>
                  </Box>
                )
              ) : (
                <>
                  <Controller
                    control={control}
                    name='supplierId'
                    rules={{ required: 'Supplier is required.' }}
                    render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                      <FormControl isRequired isInvalid={!!error}>
                        <FormLabel>Supplier</FormLabel>
                        <ChakraSelect
                          options={supplierOptions}
                          onChange={(option: any) => {
                            onChange(option);
                          }}
                          onBlur={onBlur}
                          value={value}
                          placeholder='Search supplier by name...'
                          isLoading={isSuppliersLoading}
                          onInputChange={(inputValue: string) => {
                            setSupplierSearch(inputValue);
                          }}
                          filterOption={() => true}
                          noOptionsMessage={() =>
                            supplierSearch.length < 2 ? 'Type at least 2 characters to search' : 'No suppliers found'
                          }
                        />
                        <FormErrorMessage>{error?.message}</FormErrorMessage>
                        {value && (value as TSupplierOption).countryCode && (
                          <Text fontSize='xs' color='gray.500' mt='1'>
                            Country: {(value as TSupplierOption).countryCode}
                          </Text>
                        )}
                      </FormControl>
                    )}
                  />

                  <Controller
                    control={control}
                    name='productId'
                    rules={{ required: 'Product is required.' }}
                    render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                      <FormControl isRequired isInvalid={!!error} isDisabled={!supplierId}>
                        <FormLabel>Product</FormLabel>
                        <ChakraSelect
                          options={productOptions}
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                          isDisabled={!supplierId}
                          placeholder={supplierId ? 'Select product' : 'Select supplier first'}
                          isLoading={isProductsLoading}
                        />
                        <FormErrorMessage>{error?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />

                </>
              )}

              <HStack spacing='md' alignItems='flex-end'>
                <FormControl isInvalid={!!errors.productExpiryDate}>
                  <FormLabel>Product Expiry Date</FormLabel>
                  <Input type='date' {...register('productExpiryDate')} />
                  <FormErrorMessage>{errors.productExpiryDate?.message}</FormErrorMessage>
                </FormControl>

                <FormControl display='flex' alignItems='center' minH='40px'>
                  <Switch
                    id='isNearExpiry'
                    isChecked={!!isNearExpiryValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('isNearExpiry', e.target.checked)}
                    colorScheme='red'
                    me='sm'
                  />
                  <FormLabel htmlFor='isNearExpiry' mb='0' cursor='pointer'>
                    Near Expiry
                  </FormLabel>
                </FormControl>
              </HStack>

              <HStack spacing='md'>
                <FormControl isInvalid={!!errors.cashbackPercent}>
                  <FormLabel>Cashback %</FormLabel>
                  <Input
                    type='number'
                    step='0.01'
                    {...register('cashbackPercent', {
                      min: { value: 0, message: 'Must be at least 0%' },
                      max: { value: 100, message: 'Cannot exceed 100%' },
                    })}
                  />
                  <FormErrorMessage>{errors.cashbackPercent?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.dealPrice}>
                  <FormLabel>Deal Price</FormLabel>
                  <Input
                    type='number'
                    step='0.01'
                    {...register('dealPrice', {
                      min: { value: 0, message: 'Price cannot be negative' },
                    })}
                  />
                  <FormErrorMessage>{errors.dealPrice?.message}</FormErrorMessage>
                  {!isEdit && selectedProduct?.defaultPrice !== undefined && (
                    <Text fontSize='xs' color='gray.500' mt='1'>
                      Default: {selectedProduct.defaultPrice}
                      {selectedProduct.defaultUnit && ` / ${selectedProduct.defaultUnit}`}
                    </Text>
                  )}
                  {isEdit && dealDetails?.product?.defaultPrice !== undefined && (
                    <Text fontSize='xs' color='gray.500' mt='1'>
                      Default: {dealDetails.product.defaultPrice}
                      {dealDetails.product.defaultUnit && ` / ${dealDetails.product.defaultUnit}`}
                    </Text>
                  )}
                </FormControl>
              </HStack>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='red' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='addExclusiveDealModal'
                isLoading={isCreateLoading || isUpdateLoading}>
                {isEdit ? 'Update Deal' : 'Create Deal'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
